import React, { useEffect, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { useUtils } from "../../context/UtilsContext";
import { uploadUserFile } from "../../service/AuthWellDhanService";

// Profile-redesign field styles (Claude Design "Profile Modal Redesign" 4a).
const INPUT =
  "h-10 w-full border border-[#E1DCD6] rounded-lg px-3 text-sm text-[#1F2328] bg-white outline-none focus:border-[#F2601F] focus:shadow-[0_0_0_3px_#FFE3D2]";
const LABEL = "text-[13px] font-bold text-[#1F2328]";

const EditProfileCard = ({
  isOpen,
  onClose,
  profileData,
  setProfileData,
  onSave,
  showPhotoControls = false,
  sectionFilter = null,
}) => {
  const { profileUtils } = useUtils();
  const profileOptions = profileUtils || {};
  const certificationOptions = profileOptions?.certifications || [];
  const [otherMedicalConditionInput, setOtherMedicalConditionInput] = useState("");

  // Decide if a section should render, based on optional filter.
  const showSection = (key) => !sectionFilter || sectionFilter === key;

  // Clear any unconfirmed text whenever the modal closes, so it can't leak
  // into the next edit session (this component never unmounts - see the
  // `if (!isOpen) return null;` below - so local state would otherwise persist).
  useEffect(() => {
    if (!isOpen) {
      setOtherMedicalConditionInput("");
    }
  }, [isOpen]);

  // Normalize for duplicate comparison only (display/save keep the original
  // text): Unicode-normalized + case-insensitive, and hyphens/underscores
  // treated like spaces so "Back-pain" and "Back pain" are recognized as
  // the same condition.
  const normalizeForCompare = (value) =>
    value
      .normalize("NFC")
      .toLowerCase()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const collapseWhitespace = (value) => value.replace(/\s+/g, " ").trim();

  // Strip characters that aren't part of a legitimate medical condition
  // phrase; junk symbols (# $ @ % etc.) are silently removed rather than
  // blocking the whole entry. Digits are always kept (e.g. "Type 2
  // Diabetes", "COVID-19").
  const stripDisallowedCharacters = (value) =>
    value.replace(/[^\p{L}\p{N}\s\-_'/().+&]/gu, "");

  const sanitizeConditionText = (value) =>
    collapseWhitespace(stripDisallowedCharacters(value));

  // Merge free text into the chip list: comma-split, sanitize, drop pieces
  // with no letters left, skip duplicates (normalized comparison).
  const mergeOtherMedicalConditions = (list, rawValue) => {
    const merged = [...list];
    rawValue
      .split(",")
      .map((piece) => sanitizeConditionText(piece))
      .filter((value) => value && /\p{L}/u.test(value))
      .forEach((value) => {
        if (
          !merged.some(
            (c) => normalizeForCompare(c) === normalizeForCompare(value)
          )
        ) {
          merged.push(value);
        }
      });
    return merged;
  };

  // Auto-confirm chips as soon as a comma is typed/pasted, so a chip can
  // never itself contain a comma (that's the delimiter used on the wire).
  const handleOtherMedicalConditionInputChange = (e) => {
    const rawValue = e.target.value;
    if (!rawValue.includes(",")) {
      setOtherMedicalConditionInput(rawValue);
      return;
    }
    const parts = rawValue.split(",");
    const remainder = parts.pop();
    const toAdd = parts.join(",");
    if (toAdd.trim()) {
      setProfileData((prev) => ({
        ...prev,
        otherMedicalConditions: mergeOtherMedicalConditions(
          prev.otherMedicalConditions || [],
          toAdd
        ),
      }));
    }
    setOtherMedicalConditionInput(remainder);
  };

  // Add the pending text-box value as a new chip on Enter.
  const handleAddOtherMedicalCondition = () => {
    const value = otherMedicalConditionInput;
    setOtherMedicalConditionInput("");
    if (!value.trim()) return;
    setProfileData((prev) => ({
      ...prev,
      otherMedicalConditions: mergeOtherMedicalConditions(
        prev.otherMedicalConditions || [],
        value
      ),
    }));
  };

  const handleRemoveOtherMedicalCondition = (index) => {
    setProfileData((prev) => ({
      ...prev,
      otherMedicalConditions: (prev.otherMedicalConditions || []).filter(
        (_, i) => i !== index
      ),
    }));
  };


  // ---- Certificate upload via shared helper ----
  const handleCertificationFileUpload = async (index, file) => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    // Support both PDF and images
    const isValidFile = 
      file.type === "application/pdf" || 
      file.type.startsWith("image/");
    
    if (!isValidFile) {
      alert("Please upload a valid PDF or image file (PNG, JPG, etc.).");
      return;
    }

    try {
      const response = await uploadUserFile({
        file,
        category: "certificates",
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to upload certificate.");
      }

      const uploadedUrl =
        response.data?.blob_url ||
        response.data?.blob_path ||
        response.data?.url ||
        "";
      const uploadedSasUrl =
        response.data?.blob_url_sas ||
        response.data?.certification_url_sas ||
        uploadedUrl;

      handleCertificationChange(
        index,
        "certification_url",
        uploadedUrl
      );
      handleCertificationChange(
        index,
        "certification_url_sas",
        uploadedSasUrl
      );
      handleCertificationChange(
        index,
        "certification_view_url",
        uploadedSasUrl || uploadedUrl
      );
    } catch (error) {
      console.error("Certificate upload error:", error);
      const msg =
        error?.message ||
        (error?.response?.status === 401
          ? "Session expired. Please log in again and retry."
          : "Failed to upload certificate. Please try again.");
      alert(msg);
    }
  };


  const handleAddCertification = () => {
    setProfileData((prev) => ({
      ...prev,
      certifications: [
        ...(prev.certifications || []),
        {
          certification_id: "",
          certification_url: "",
          certification_url_sas: "",
          certification_view_url: "",
          other_name: "",
          description: "",
          date_received: "",
        },
      ],
    }));
  };

  const handleCertificationChange = (index, field, value) => {
    setProfileData((prev) => {
      const updated = [...(prev.certifications || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, certifications: updated };
    });
  };

  const handleRemoveCertification = (index) => {
    setProfileData((prev) => {
      const updated = [...(prev.certifications || [])];
      updated.splice(index, 1);
      return { ...prev, certifications: updated };
    });
  };

  // Multi-select language handlers
  const handleLanguageSelect = (languageId) => {
    if (!languageId) return;
    
    const language = profileOptions?.languages?.find(l => l.id === languageId);
    if (!language) return;

    // Check if already selected
    const isAlreadySelected = profileData.languages?.some(
      l => l.language_id === languageId || l.id === languageId
    );

    if (isAlreadySelected) return;

    setProfileData((prev) => ({
      ...prev,
      languages: [
        ...(prev.languages || []),
        { 
          language_id: languageId, 
          id: languageId,
          description: language.description,
          other_name: "",
          existing_id: null // New language, no existing_id yet
        },
      ],
    }));
  };

  const handleRemoveLanguage = (languageId, _existingId) => {
    setProfileData((prev) => {
      const updated = (prev.languages || []).filter(
        l => (l.language_id || l.id) !== languageId
      );
      return { ...prev, languages: updated };
    });
  };

  // Multi-select specialty handlers
  const handleSpecialtySelect = (specialtyId) => {
    if (!specialtyId) return;
    
    const specialty = profileOptions?.professional_specialties?.find(s => s.id === specialtyId);
    if (!specialty) return;

    // Check if already selected
    const isAlreadySelected = profileData.professionalSpecialties?.some(
      s => (s.professional_specialty_id || s.professional_speciality_id || s.id) === specialtyId
    );

    if (isAlreadySelected) return;

    setProfileData((prev) => ({
      ...prev,
      professionalSpecialties: [
        ...(prev.professionalSpecialties || []),
        { 
          professional_specialty_id: specialtyId,
          professional_speciality_id: specialtyId,
          id: specialtyId,
          description: specialty.description,
          other_name: "",
          existing_id: null // New specialty, no existing_id yet
        },
      ],
    }));
  };

  const handleRemoveSpecialty = (specialtyId, _existingId) => {
    setProfileData((prev) => {
      const updated = (prev.professionalSpecialties || []).filter(
        s => (s.professional_specialty_id || s.professional_speciality_id || s.id) !== specialtyId
      );
      return { ...prev, professionalSpecialties: updated };
    });
  };

  const handleAddAchievement = () => {
    setProfileData((prev) => ({
      ...prev,
      achievements: [
        ...(prev.achievements || []),
        { name: "", achievement_url: "", description: "", date_achieved: "" },
      ],
    }));
  };

  const handleAchievementChange = (index, field, value) => {
    setProfileData((prev) => {
      const updated = [...(prev.achievements || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, achievements: updated };
    });
  };

  const handleRemoveAchievement = (index) => {
    setProfileData((prev) => {
      const updated = [...(prev.achievements || [])];
      updated.splice(index, 1);
      return { ...prev, achievements: updated };
    });
  };

  if (!isOpen) return null;

  // Toggle selection for chips (diet, medical, goals).
  const toggleSelection = (field, item) => {
    const existing = profileData[field] || [];
    const exists = existing.some((f) => f.id === item.id);
    const updated = exists
      ? existing.filter((f) => f.id !== item.id)
      : [...existing, { id: item.id, description: item.description }];

    setProfileData((prev) => ({ ...prev, [field]: updated }));
  };

  // Bubble save to parent handler.
  const handleSave = async () => {
    // Flush any unconfirmed other-medical-condition text (typed but Enter
    // never pressed) so it isn't silently discarded. `onSave` closes over
    // the parent's profileData from render time, so a plain setProfileData
    // here wouldn't be visible to it in time - pass the final value explicitly
    // instead, mirroring how photo uploads already dodge this same race.
    const pendingValue = otherMedicalConditionInput;
    const finalOtherMedicalConditions = pendingValue.trim()
      ? mergeOtherMedicalConditions(profileData.otherMedicalConditions || [], pendingValue)
      : profileData.otherMedicalConditions || [];

    if (pendingValue.trim()) {
      setProfileData((prev) => ({
        ...prev,
        otherMedicalConditions: finalOtherMedicalConditions,
      }));
      setOtherMedicalConditionInput("");
    }

    onClose();
    await onSave(null, { otherMedicalConditions: finalOtherMedicalConditions });
  };

  // Check if chip is currently selected.
  const isSelected = (field, itemId) => {
    return profileData?.[field]?.some((item) => item.id === itemId) || false;
  };

  // Modal title: the full/basic editor is "Edit profile"; a section-scoped
  // editor (opened from the Professional tab) names its section.
  const SECTION_TITLES = {
    certifications: "Certifications",
    languages: "Languages",
    professionalSpecialties: "Professional specialties",
    achievements: "Achievements",
  };
  const modalTitle = SECTION_TITLES[sectionFilter] || "Edit profile";

  const openPdfInNewTab = (url) => {
    if (!url) return;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      alert("Please allow popups to view the PDF.");
    }
  };

  const genderOptions = profileOptions?.genders || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-[rgba(31,35,40,0.4)] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[14px] shadow-[0_24px_60px_rgba(31,35,40,0.28)] w-full max-w-[560px] max-h-[85vh] flex flex-col"
      >
        <div className="px-[22px] py-[18px] flex items-center border-b border-[#EFEBE7]">
          <h2 id="edit-profile-title" className="flex-1 text-[17px] font-extrabold text-[#1F2328]">
            {modalTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${modalTitle}`}
            className="w-8 h-8 rounded-lg bg-[#F4F1EE] text-[#5C554F] flex items-center justify-center hover:bg-[#EFEBE7]"
          >
            <IoIosClose className="w-6 h-6" />
          </button>
        </div>

        <div className="px-[22px] py-5 flex flex-col gap-[14px] overflow-y-auto min-h-0 flex-1">
        {showSection("basic") && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>First name</span>
                <input
                  type="text"
                  value={profileData.firstName || ""}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  className={INPUT}
                  placeholder="Enter first name"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Last name</span>
                <input
                  type="text"
                  value={profileData.lastName || ""}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  className={INPUT}
                  placeholder="Enter last name"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <span className={LABEL}>Gender</span>
                {genderOptions.length > 0 ? (
                  <div
                    role="radiogroup"
                    aria-label="Gender"
                    className="grid gap-0.5 bg-[#F4F1EE] rounded-lg p-[3px] h-10"
                    style={{ gridTemplateColumns: `repeat(${genderOptions.length}, minmax(0, 1fr))` }}
                  >
                    {genderOptions.map((g) => {
                      const on = profileData.gender === g.description;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() =>
                            setProfileData((prev) => ({ ...prev, gender: g.description }))
                          }
                          className={`rounded-md text-[13px] font-bold truncate px-1 ${
                            on
                              ? "bg-white text-[#1F2328] shadow-[0_1px_2px_rgba(31,35,40,0.12)]"
                              : "text-[#8A8178]"
                          }`}
                        >
                          {g.description}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <input value={profileData.gender || ""} disabled className={INPUT} />
                )}
              </div>
              <label className="flex flex-col gap-1.5">
                <span className={LABEL}>Date of birth</span>
                <input
                  type="date"
                  value={profileData.date_of_birth?.slice(0, 10) || ""}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, date_of_birth: e.target.value }))
                  }
                  className={INPUT}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                ["Weight", "weight", "weightUnits", profileOptions?.weight_units, "e.g. 72"],
                ["Height", "height", "heightUnits", profileOptions?.height_units, "e.g. 175"],
              ].map(([label, field, unitField, units, placeholder]) => (
                <label key={field} className="flex flex-col gap-1.5">
                  <span className={LABEL}>{label}</span>
                  <div className="flex items-center h-10 border border-[#E1DCD6] rounded-lg pl-3 pr-1 gap-1.5 focus-within:border-[#F2601F] focus-within:shadow-[0_0_0_3px_#FFE3D2]">
                    <input
                      type="number"
                      value={profileData[field] || ""}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          [field]: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder={placeholder}
                      min="0"
                      step="0.1"
                      className="flex-1 min-w-0 border-none text-sm outline-none bg-transparent"
                    />
                    <select
                      value={profileData[unitField] || ""}
                      onChange={(e) =>
                        setProfileData((prev) => ({ ...prev, [unitField]: e.target.value }))
                      }
                      aria-label={`${label} unit`}
                      className="text-[13px] text-[#8A8178] font-bold bg-transparent border-none outline-none cursor-pointer"
                    >
                      {(units || []).map((unit) => (
                        <option key={unit.id} value={unit.description}>
                          {unit.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
              ))}
            </div>
            <span className="text-xs text-[#8A8178] -mt-1">
              Weight and height are used to calculate BMI, BMR and TDEE.
            </span>

            <div className="h-px bg-[#EFEBE7] my-1" />

            <label className="flex flex-col gap-1.5">
              <span className={LABEL}>Physical injury</span>
              <select
                value={profileData.physicalInjury || ""}
                onChange={(e) =>
                  setProfileData((prev) => ({ ...prev, physicalInjury: e.target.value }))
                }
                className={`${INPUT} px-2.5`}
              >
                <option value="None">None</option>
                {profileOptions?.physical_injuries?.map((inj) => (
                  <option key={inj.id} value={inj.description}>
                    {inj.description}
                  </option>
                ))}
              </select>
            </label>

            {[
              {
                label: "Dietary preferences",
                field: "dietaryPreferences",
                data: profileOptions.dietary_preferences,
              },
              {
                label: "Medical history",
                field: "medicalHistories",
                data: profileOptions.medical_histories,
              },
              {
                label: "Fitness goals",
                field: "fitnessGoals",
                data: profileOptions.fitness_goals,
              },
            ].map(({ label, field, data }) => {
              const count = profileData?.[field]?.length || 0;
              return (
                <div className="flex flex-col gap-2" key={field}>
                  <div className="flex items-baseline justify-between">
                    <span className={LABEL}>{label}</span>
                    <span className="text-xs text-[#8A8178]">
                      {count ? `${count} selected` : "None selected"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(data || []).map((item) => {
                      const selected = isSelected(field, item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => toggleSelection(field, item)}
                          className={`h-8 px-3 rounded-full text-[13px] font-semibold border ${
                            selected
                              ? "border-[#F2601F] bg-[#FFF0E6] text-[#C2410C]"
                              : "border-[#E1DCD6] bg-white text-[#5C554F] hover:bg-[#FAF8F6]"
                          }`}
                        >
                          {selected ? `✓ ${item.description}` : item.description}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex flex-col gap-2">
              <span className={LABEL}>Other medical conditions</span>
              <div className="min-h-10 border border-[#E1DCD6] rounded-lg px-2 py-[5px] flex flex-wrap items-center gap-1.5 focus-within:border-[#F2601F] focus-within:shadow-[0_0_0_3px_#FFE3D2]">
                {(profileData.otherMedicalConditions || []).map((condition, idx) => (
                  <span
                    key={idx}
                    className="h-7 pl-2.5 pr-1 rounded-full bg-[#FFF0E6] text-[#C2410C] text-[13px] font-bold flex items-center gap-1"
                  >
                    {condition}
                    <button
                      type="button"
                      onClick={() => handleRemoveOtherMedicalCondition(idx)}
                      aria-label={`Remove ${condition}`}
                      className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-[#FFE3D2]"
                    >
                      <IoIosClose className="w-4 h-4" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={otherMedicalConditionInput}
                  onChange={handleOtherMedicalConditionInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleAddOtherMedicalCondition();
                    }
                    if (
                      e.key === "Backspace" &&
                      !otherMedicalConditionInput &&
                      (profileData.otherMedicalConditions || []).length
                    ) {
                      handleRemoveOtherMedicalCondition(
                        profileData.otherMedicalConditions.length - 1
                      );
                    }
                  }}
                  placeholder="Type a condition and press Enter"
                  maxLength={150}
                  className="flex-1 min-w-[180px] h-7 border-none text-sm outline-none px-1 bg-transparent"
                />
              </div>
            </div>
          </>
        )}

        {showPhotoControls && showSection("certifications") && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <label className="block text-xl font-bold text-gray-900">
                Certifications
              </label>
              <button
                type="button"
                onClick={handleAddCertification}
                className="px-4 py-2 text-sm font-semibold text-white bg-orange-primary hover:bg-orange-600 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add certification
              </button>
            </div>
            <div className="space-y-5">
              {(profileData.certifications || []).map((cert, idx) => {
                const isOtherSelected = cert.certification_id === "other" || 
                  (cert.certification_id && certificationOptions.find(c => c.id === cert.certification_id && c.description?.toLowerCase() === "other"));
                const showOtherFields = isOtherSelected;
                
                return (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-xl p-6 bg-white shadow-md hover:shadow-lg transition-shadow duration-200 relative"
                  >
                    {/* Delete Button at Top Right */}
                    <button
                      type="button"
                      onClick={() => handleRemoveCertification(idx)}
                      className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition-all duration-200 hover:scale-110"
                      aria-label="Delete certification"
                      title="Delete certification"
                    >
                      <IoIosClose className="w-5 h-5" />
                    </button>

                    {/* Certification Type and Date Received - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 pr-10">
                      {/* Certification Type */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Certification Type
                        </label>
                        <select
                          value={cert.certification_id || ""}
                          onChange={(e) =>
                            handleCertificationChange(
                              idx,
                              "certification_id",
                              e.target.value
                            )
                          }
                          className="w-full border border-gray-300 px-4 py-2.5 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm transition-all duration-200"
                        >
                          <option value="">
                            {certificationOptions.length
                              ? "Select Certification"
                              : "No certifications available"}
                          </option>
                          {certificationOptions.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description}
                            </option>
                          ))}
                          <option value="other">Other</option>
                        </select>
                      </div>

                      {/* Date Received */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Date Received
                        </label>
                        <input
                          type="date"
                          value={cert.date_received?.slice(0, 10) || ""}
                          onChange={(e) =>
                            handleCertificationChange(
                              idx,
                              "date_received",
                              e.target.value
                            )
                          }
                          className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* Upload PDF/Image - Enhanced Design */}
                    <div className="mb-5">
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        Upload Certificate (PDF or Image)
                      </label>
                      <div className="relative">
                        {(cert.certification_view_url ||
                          cert.certification_url_sas ||
                          cert.certification_url) ? (
                          // File Uploaded State
                          <div className="border-2 border-orange-200 rounded-xl bg-orange-50 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                {/* File Type Icon */}
                                {cert.certification_url?.includes('.pdf') || 
                                 cert.certification_view_url?.includes('.pdf') ? (
                                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-7 h-7 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center overflow-hidden">
                                    <img 
                                      src={cert.certification_view_url || cert.certification_url_sas || cert.certification_url} 
                                      alt="Certificate preview" 
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="w-full h-full bg-orange-100 flex items-center justify-center" style={{display: 'none'}}>
                                      <svg className="w-7 h-7 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {cert.certification_url?.split('/').pop() || 'Certificate file'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {cert.certification_url?.includes('.pdf') ? 'PDF Document' : 'Image File'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    openPdfInNewTab(
                                      cert.certification_url_sas ||
                                        cert.certification_view_url ||
                                        cert.certification_url
                                    )
                                  }
                                  className="px-3 py-1.5 text-sm font-medium text-orange-primary hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                                >
                                  View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleCertificationChange(idx, "certification_url", "");
                                    handleCertificationChange(idx, "certification_url_sas", "");
                                    handleCertificationChange(idx, "certification_view_url", "");
                                  }}
                                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Upload Area
                          <label className="cursor-pointer flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-orange-50 hover:to-orange-100 hover:border-orange-400 transition-all duration-300 group">
                            <div className="flex flex-col items-center justify-center">
                              <div className="w-16 h-16 mb-4 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:scale-110 transition-all duration-300">
                                <svg
                                  className="w-8 h-8 text-gray-400 group-hover:text-orange-primary transition-colors duration-300"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                              </div>
                              <p className="mb-1 text-sm font-medium text-gray-700 group-hover:text-orange-primary transition-colors duration-300">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, PNG, JPG (MAX. 10MB)
                              </p>
                            </div>
                            <input
                              type="file"
                              accept="application/pdf,image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.type === "application/pdf") {
                                    handleCertificationFileUpload(idx, file);
                                  } else if (file.type.startsWith("image/")) {
                                    handleCertificationFileUpload(idx, file);
                                  } else {
                                    alert("Please upload a PDF or image file.");
                                  }
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Other Name - Only show when "Other" is selected with smooth transition */}
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        showOtherFields 
                          ? 'max-h-32 opacity-100 mb-5' 
                          : 'max-h-0 opacity-0 mb-0'
                      }`}
                    >
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Other Name
                        </label>
                        <input
                          type="text"
                          value={cert.other_name || ""}
                          onChange={(e) =>
                            handleCertificationChange(
                              idx,
                              "other_name",
                              e.target.value
                            )
                          }
                          className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm transition-all duration-200"
                          placeholder="e.g., Custom Certification Name"
                        />
                      </div>
                    </div>

                    {/* Description - Full width at bottom */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={cert.description || ""}
                        onChange={(e) =>
                          handleCertificationChange(
                            idx,
                            "description",
                            e.target.value
                          )
                        }
                        rows={3}
                        className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm resize-none transition-all duration-200"
                        placeholder="Additional details about this certification"
                      />
                    </div>
                  </div>
                );
              })}
              {!(profileData.certifications || []).length && (
                <div className="text-center py-12 px-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-base font-medium text-gray-600 mb-1">No certifications added yet</p>
                  <p className="text-sm text-gray-500">Click "Add certification" to get started</p>
                </div>
              )}
            </div>
          </div>
        )}

        {showPhotoControls && showSection("languages") && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-lg font-semibold">Languages</label>
            </div>
            
            {/* Multi-select Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Languages
              </label>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleLanguageSelect(e.target.value);
                    e.target.value = ""; // Reset dropdown
                  }
                }}
                className="w-full border border-gray-300 px-3 py-2 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm"
              >
                <option value="">
                  {(profileOptions?.languages || []).length
                    ? "Choose a language to add..."
                    : "No languages available"}
                </option>
                {(profileOptions?.languages || []).map((l) => {
                  const isSelected = profileData.languages?.some(
                    lang => (lang.language_id || lang.id) === l.id
                  );
                  return (
                    <option 
                      key={l.id} 
                      value={l.id}
                      disabled={isSelected}
                    >
                      {l.description} {isSelected ? "(Already added)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Selected Languages as Chips */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[100px]">
              {(profileData.languages || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(profileData.languages || [])
                    .map((lang, idx) => {
                      const languageId = lang.language_id || lang.id;
                      const languageName = lang.description || 
                        lang.other_name ||
                        profileOptions?.languages?.find(l => l.id === languageId)?.description || 
                        null;
                      return { lang, idx, languageName, languageId };
                    })
                    .filter(({ languageName }) => languageName && languageName.trim() !== "")
                    .map(({ lang, idx, languageName, languageId }) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <span>{languageName}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveLanguage(languageId, lang.existing_id)}
                          className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                          aria-label={`Remove ${languageName}`}
                        >
                          <IoIosClose className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No languages selected. Use the dropdown above to add languages.
                </p>
              )}
            </div>
          </div>
        )}

        {showPhotoControls && showSection("professionalSpecialties") && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-lg font-semibold">
                Professional Specialties
              </label>
            </div>
            
            {/* Multi-select Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Specialties
              </label>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleSpecialtySelect(e.target.value);
                    e.target.value = ""; // Reset dropdown
                  }
                }}
                className="w-full border border-gray-300 px-3 py-2 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm"
              >
                <option value="">
                  {(profileOptions?.professional_specialties || []).length
                    ? "Choose a specialty to add..."
                    : "No specialties available"}
                </option>
                {(profileOptions?.professional_specialties || []).map((s) => {
                  const isSelected = profileData.professionalSpecialties?.some(
                    spec => (spec.professional_specialty_id || spec.professional_speciality_id || spec.id) === s.id
                  );
                  return (
                    <option 
                      key={s.id} 
                      value={s.id}
                      disabled={isSelected}
                    >
                      {s.description} {isSelected ? "(Already added)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Selected Specialties as Chips */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[100px]">
              {(profileData.professionalSpecialties || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(profileData.professionalSpecialties || [])
                    .map((spec, idx) => {
                      const specialtyId = spec.professional_specialty_id || spec.professional_speciality_id || spec.id;
                      const specialtyName = spec.description || 
                        spec.other_name ||
                        profileOptions?.professional_specialties?.find(s => s.id === specialtyId)?.description || 
                        null;
                      return { spec, idx, specialtyName, specialtyId };
                    })
                    .filter(({ specialtyName }) => specialtyName && specialtyName.trim() !== "")
                    .map(({ spec, idx, specialtyName, specialtyId }) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <span>{specialtyName}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecialty(specialtyId, spec.existing_id)}
                          className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                          aria-label={`Remove ${specialtyName}`}
                        >
                          <IoIosClose className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No specialties selected. Use the dropdown above to add specialties.
                </p>
              )}
            </div>
          </div>
        )}

        {showPhotoControls && showSection("achievements") && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-lg font-semibold">
                Achievements
              </label>
              <button
                type="button"
                onClick={handleAddAchievement}
                className="text-sm font-medium text-orange-primary hover:text-orange-600 transition-colors"
              >
                Add achievement
              </button>
            </div>
            <div className="space-y-4">
              {(profileData.achievements || []).map((ach, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm relative"
                >
                  {/* Delete Button at Top Right */}
                  <button
                    type="button"
                    onClick={() => handleRemoveAchievement(idx)}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition-colors"
                    aria-label="Delete achievement"
                    title="Delete achievement"
                  >
                    <IoIosClose className="w-5 h-5" />
                  </button>

                  {/* Description - Primary Field */}
                  <div className="mb-4 pr-10">
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Description <span className="text-orange-primary">*</span>
                    </label>
                    <textarea
                      value={ach.description || ""}
                      onChange={(e) =>
                        handleAchievementChange(
                          idx,
                          "description",
                          e.target.value
                        )
                      }
                      rows={4}
                      className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent shadow-sm resize-none"
                      placeholder="Describe your achievement in detail..."
                    />
                  </div>

                  {/* Name - Placeholder Style (Secondary) */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium mb-1 text-gray-500">
                      Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={ach.name || ""}
                      onChange={(e) =>
                        handleAchievementChange(idx, "name", e.target.value)
                      }
                      className="w-full border border-gray-200 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent text-sm text-gray-600"
                      placeholder="e.g., Award name or title"
                    />
                  </div>

                  {/* Date and URL - Secondary Fields (Lower Importance) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-500">
                        Date Achieved (Optional)
                      </label>
                      <input
                        type="date"
                        value={ach.date_achieved?.slice(0, 10) || ""}
                        onChange={(e) =>
                          handleAchievementChange(
                            idx,
                            "date_achieved",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-200 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-500">
                        URL (Optional)
                      </label>
                      <input
                        type="text"
                        value={ach.achievement_url || ""}
                        onChange={(e) =>
                          handleAchievementChange(
                            idx,
                            "achievement_url",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-200 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent text-sm text-gray-600"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              ))}
              {!(profileData.achievements || []).length && (
                <p className="text-sm text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  No achievements added yet. Click "Add achievement" to get started.
                </p>
              )}
            </div>
          </div>
        )}

        </div>

        <div className="px-[22px] py-[14px] border-t border-[#EFEBE7] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-[38px] px-4 rounded-lg border border-[#E8E5E1] bg-white text-sm font-bold text-[#1F2328] hover:bg-[#FAF8F6]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="h-[38px] px-[18px] rounded-lg bg-[#F2601F] text-white text-sm font-bold hover:bg-[#E8590C]"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfileCard;
